import apiClient from './apiClient';
import PracticeSettingsService from './PracticeSettingsService';

export function getPracticePlayerTtsSource(card) {
  return PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING
    ? 'content'
    : 'answer';
}

export function getPracticePlayerTtsSources(card) {
  return PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING
    ? ['content']
    : ['question', 'answer'];
}

function isPublicAudioUrl(audioUrl) {
  if (!/^https?:\/\//i.test(String(audioUrl || ''))) return false;
  try {
    return !new URL(audioUrl).pathname.includes('/api/tts/assets/');
  } catch (_) {
    return false;
  }
}

function canRetryWithoutSource(error) {
  return error?.status === 400 || error?.status === 404 || error?.status === 422;
}

async function requestWithSourceFallback(request, cardId, source, signal) {
  try {
    return await request(cardId, { source, signal });
  } catch (error) {
    // An older API without question-source support could return the answer here,
    // which is worse than reporting the card as unavailable.
    if (source === 'question' || !canRetryWithoutSource(error)) throw error;
    return request(cardId, { signal });
  }
}

async function withPreparationTimeout(callback, externalSignal, timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  if (externalSignal?.aborted) {
    throw Object.assign(new Error('Player closed'), { name: 'AbortError' });
  }
  externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(Object.assign(new Error('Listening audio preparation timed out. Please try again.'), {
        code: 'TTS_PREPARATION_TIMEOUT',
      }));
    }, timeoutMs);
  });

  try {
    return await Promise.race([callback(controller.signal), timeout]);
  } catch (error) {
    if (timedOut && error?.code !== 'TTS_PREPARATION_TIMEOUT') {
      throw Object.assign(new Error('Listening audio preparation timed out. Please try again.'), {
        code: 'TTS_PREPARATION_TIMEOUT',
      });
    }
    throw error;
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener('abort', abortFromCaller);
  }
}

async function prepareSourceAudio(cardId, source, { signal, timeoutMs }) {
  return withPreparationTimeout(async (requestSignal) => {
    let result = await requestWithSourceFallback(apiClient.getCardTtsStatus, cardId, source, requestSignal);
    if (result?.status === 'unavailable') {
      throw Object.assign(new Error(result.message || 'Listening audio is unavailable.'), {
        code: result.reasonCode || 'TTS_UNAVAILABLE',
      });
    }
    if (result?.status !== 'ready') {
      result = await requestWithSourceFallback(apiClient.generateCardTts, cardId, source, requestSignal);
    }
    if (!result?.audio?.url) throw new Error('Listening audio is unavailable.');

    if (isPublicAudioUrl(result.audio.url)) {
      return { audioInfo: result.audio, audioUrl: result.audio.url, blob: null, source };
    }

    const blob = await apiClient.fetchTtsAudioBlob(result.audio.url, { signal: requestSignal });
    return { audioInfo: result.audio, audioUrl: null, blob, source };
  }, signal, timeoutMs);
}

export async function preparePracticePlayerAudio(card, { signal, timeoutMs = 30000 } = {}) {
  const cardId = card?.id ?? card?._id;
  if (!cardId) throw new Error('This card has no ID.');
  const sources = getPracticePlayerTtsSources(card);

  if (sources[0] === 'content') {
    return {
      kind: 'reading',
      cardId,
    };
  }

  const question = await prepareSourceAudio(cardId, 'question', { signal, timeoutMs });
  const answer = await prepareSourceAudio(cardId, 'answer', { signal, timeoutMs });
  return { kind: 'flashcard', question, answer };
}

const PracticePlayerAudioService = {
  getPracticePlayerTtsSource,
  getPracticePlayerTtsSources,
  isPublicAudioUrl,
  preparePracticePlayerAudio,
};

export default PracticePlayerAudioService;
